<script setup lang="ts">
import { StrapiBanner, UiHeadingSection } from '#components'
import type { BannerTypeFragment } from '#graphql-operations'
import { LocationOpeningHours } from '~layers/utils/locationOpeningHours.ts'

const meta = inject('meta', ref())
const storeId = computed(() => {
  return meta.value?.filters?.find(filter => filter.key === 'id')?.values?.at(0)
})

const { public: config } = useRuntimeConfig()
const locale = config.strapiLocale
const strapiData = ref<T | null>()
const strapiError = ref<Error | null | undefined>(null)

const { data: response } = await useAsyncGraphqlQuery('storeDetail', {
  documentId: meta?.value?.strapi_id || '',
  locale,
  status: 'PUBLISHED',
  storeCode: `${storeId.value}`,
}, useGraphqlDefaultClient())

strapiData.value = response?.value?.data

const locationWithOpeningHours = computed(() => {
  return new LocationOpeningHours(strapiData.value?.ttLocation)
})

if (response?.value?.errors?.length) {
  strapiError.value = response.value.errors
  console.error('getStrapiData', response.value.errors)
}

const { $t } = useI18n()

const pageTitle = computed(
  () => strapiData.value?.page?.PageTitle || meta.value.label || '',
)

const metaTitle = computed(
  () => strapiData.value?.page?.MetaData?.MetaTitle || pageTitle.value + ' | ' + $t('meta.title'),
)

const metaDescription = computed(
  () => strapiData.value?.page?.MetaData?.MetaDescription || $t('meta.description'),
)

const storeBanner = computed(() => {
  if (!strapiData.value?.page?.Image) return null

  const imageData = strapiData.value.page.Image
  const imageMobileData = strapiData.value?.page.ImageMobile

  return {
    ButtonText: undefined,
    ButtonType: undefined,
    File: {
      Media: {
        url: imageData.url,
        width: imageData.width,
        height: imageData.height,
        mime: imageData.mime,
        alternativeText: imageData?.alternativeText,
      },
      MediaMobile: imageMobileData
        ? {
            url: imageMobileData.url,
            width: imageMobileData.width,
            height: imageMobileData.height,
            mime: imageMobileData.mime,
          }
        : undefined,
    },
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } as any as BannerTypeFragment
})

const { setBreadcrumbs } = useBreadcrumbs()
setBreadcrumbs({
  label: pageTitle.value,
  parents: meta.value.parents || [],
})

const { pageView } = useDataLayer()
pageView('store-detail', metaTitle.value)

useHead({
  title: metaTitle,
  meta: [
    { name: 'description', content: metaDescription },
    { name: 'robots', content: strapiData.value?.page?.MetaData?.MetaRobots?.replace('_', ', ') },
  ],
})

const route = useRoute()
const { canonical } = useSeo()
canonical(computed(() => route.path))
</script>

<template>
  <div class="store-detail">
    <AppBreadcrumbs v-if="$route.path !== '/'" />

    <div class="store-detail__hero">
      <StrapiBanner
        v-if="storeBanner"
        class="store__hero-banner banner--hero"
        :banner="storeBanner"
        :lazy="false"
      />
    </div>

    <StrapiSlice
      class="store-detail__data"
      :slice="{
        Settings: {
          BackgroundColor: '#fff',
          SliceWidth: 'large-column',
          Title: meta.name,
        },
      }"
      :index="0"
      variant="horizontal"
      force-heading-tag="h1"
    >
      <div class="store-detail__summary">
        <client-only>
          <div class="store-detail__today">
            <NuxtIcon name="icon:clock" />
            <span v-if="!locationWithOpeningHours.today || !locationWithOpeningHours.today.isOpen">{{ $t('Vandaag gesloten') }}</span>
            <span v-else>
              {{ $t('Vandaag geopend') }}
              <template
                v-for="(slot, index) in locationWithOpeningHours.today.slots"
                :key="index"
              >
                <template v-if="index > 0"> {{ $t('en') }} </template>
                {{ slot.open }} {{ $t('tot') }} {{ slot.close }}
              </template>
            </span>
          </div>
        </client-only>

        <div class="store-detail__address">
          <NuxtIcon name="icon:location" />
          <span>
            {{ strapiData?.ttLocation?.locationLine1 }}
            {{ strapiData?.ttLocation?.locationLine2 }},
            {{ strapiData?.ttLocation?.city }}
          </span>
        </div>
      </div>

      <div class="store-detail__actions">
        <MapRouteButton
          v-if="strapiData?.ttLocation?.lat && strapiData?.ttLocation?.lng"
          class="store-detail__route-button"
          :coords="{ lat: strapiData.ttLocation.lat, lng: strapiData.ttLocation.lng }"
        >
          <NuxtIcon name="icon:my-location" />
          <span>{{ $t('Plan your route') }}</span>
        </MapRouteButton>

        <a
          href="#opening-hours"
          class="store-detail__anchor"
        >
          {{ $t('View opening hours') }}
        </a>
      </div>

      <StoreActions
        v-if="strapiData.ttLocation"
        v-slot="{ doAction, orderStatusUrl, serviceUrl, vacanciesUrl }"
        :store="strapiData.ttLocation"
        class="store-detail__panels"
      >
        <div class="store-detail__panel">
          <div class="store-detail__panel-section">
            <UiHeading force-tag="div">
              {{ $t('Contact information') }}
            </UiHeading>

            <ul class="store-detail__contact-details">
              <li v-if="strapiData?.ttLocation?.mobileNumber">
                <a
                  :href="`tel:${strapiData.ttLocation.mobileNumber}`"
                  class="store-detail__contact-link"
                >
                  <NuxtIcon name="icon:whatsapp" />
                  {{ strapiData.ttLocation.mobileNumber }} ({{ $t('Whatsapp') }})
                </a>
              </li>

              <li v-if="strapiData?.ttLocation?.phoneNumber">
                <a
                  :href="`tel:${strapiData.ttLocation.phoneNumber}`"
                  class="store-detail__contact-link"
                >
                  <NuxtIcon name="icon:phone" />
                  {{ strapiData.ttLocation.phoneNumber }} ({{ $t('Store') }})
                </a>
              </li>

              <li v-if="strapiData?.ttLocation?.phoneNumberService">
                <a
                  :href="`tel:${strapiData.ttLocation.phoneNumberService}`"
                  class="store-detail__contact-link"
                >
                  <NuxtIcon name="icon:phone" />
                  {{ strapiData.ttLocation.phoneNumberService }} <template v-if="strapiData?.ttLocation?.phoneNumberServiceDescription">({{ strapiData.ttLocation.phoneNumberServiceDescription }})</template>
                </a>
              </li>

              <li v-if="strapiData?.ttLocation?.extraTelephoneNumber">
                <a
                  :href="`tel:${strapiData.ttLocation.extraTelephoneNumber}`"
                  class="store-detail__contact-link"
                >
                  <NuxtIcon name="icon:phone" />
                  {{ strapiData.ttLocation.extraTelephoneNumber }} <template v-if="strapiData?.ttLocation?.extraTelephoneNumberReason">({{ strapiData.ttLocation.extraTelephoneNumberReason }})</template>
                </a>
              </li>

              <li v-if="strapiData?.ttLocation?.email">
                <a
                  :href="`mailto:${strapiData.ttLocation.email}`"
                  class="store-detail__contact-link"
                >
                  <NuxtIcon name="icon:mail" />
                  {{ strapiData.ttLocation.email }}
                </a>
              </li>

              <li>
                <button
                  type="button"
                  class="store-detail__contact-link"
                  @click="doAction('Contact')"
                >
                  <NuxtIcon name="icon:chat" />
                  {{ $t('Ask your question') }}
                </button>
              </li>
            </ul>
          </div>

          <div class="store-detail__panel-section store-detail__extra">
            <UiHeading force-tag="div">
              {{ $t('Extra informatie') }}
            </UiHeading>
            <ul class="store-detail__extra-details">
              <li v-if="orderStatusUrl">
                <AppLink :to="orderStatusUrl">
                  {{ $t('Check your order status') }}
                </AppLink>
              </li>

              <li v-if="serviceUrl">
                <AppLink
                  :to="serviceUrl"
                >
                  {{ $t('Book a service appointment') }}
                </AppLink>
              </li>

              <li v-if="strapiData?.ttLocation?.adviceUrl">
                <button
                  type="button"
                  @click="doAction('Advice')"
                >
                  {{ $t('Book your consultation') }}
                </button>
              </li>
              <li v-if="strapiData?.ttLocation?.pickupUrl">
                <button
                  type="button"
                  @click="doAction('Pickup')"
                >
                  {{ $t('Book your collection appointment') }}
                </button>
              </li>
              <li v-if="vacanciesUrl">
                <AppLink :to="vacanciesUrl">
                  {{ $t('View vacancies at {name}', { name: strapiData.ttLocation.city }) }}
                </AppLink>
              </li>
            </ul>
          </div>

          <div class="store-detail__panel-section">
            <div class="store-detail__location">
              <img
                :src="`/static-map/${strapiData.ttLocation.code}/`"
                class="store-detail__map"
                alt=""
                width="424"
                height="240"
                loading="lazy"
                fetchpriority="low"
              >

              <img
                :src="`${$config.public.baseDomain}/images/marker.png`"
                class="store-detail__marker"
                alt=""
                loading="lazy"
                fetchpriority="low"
                style=""
              >
              <MapRouteButton
                v-if="strapiData?.ttLocation?.lat && strapiData?.ttLocation?.lng"
                class="store-detail__map-link"
                :coords="{ lat: strapiData.ttLocation.lat, lng: strapiData.ttLocation.lng }"
              >
                {{ $t('Open in Maps') }}
              </MapRouteButton>
            </div>
          </div>
        </div>

        <StoreOpeningHours
          id="opening-hours"
          :location="locationWithOpeningHours"
          class="store-detail__panel"
        />

        <div class="store-detail__panel store-detail__visual">
          <StrapiBanner
            v-if="storeBanner"
            class="store-detail__banner"
            :banner="storeBanner"
            :lazy="false"
          />
        </div>
      </StoreActions>
    </StrapiSlice>

    <UiHeadingSection>
      <StrapiSlices
        v-if="strapiData?.page?.Slices?.length"
        :slices="strapiData.page.Slices"
        :slice-offset="1"
      />
    </UiHeadingSection>

    <!--  -->
  </div>
</template>

<style>
@import url("~layers/pages/~store-detail.css");
</style>
